import React, { useContext, useRef, useState } from 'react';
import LazyTable from 'app/component/lazy-table';
import { formatDateFromInstant, formatFromLocalDate } from 'app/shared/util/date-utils';
import { useNavigate } from 'react-router-dom';
import { boldNameFormatter } from 'app/shared/util/formatters';
import { Button } from 'primereact/button';
import { ToastContext } from 'app/context/ToastContext';
import { exportAnniversaryContractData } from 'app/api/contractAnniversaryApi';
import { hasAnyAuthority } from 'app/shared/auth/private-route';
import { useAppSelector } from 'app/config/store';
import { AUTHORITIES } from 'app/config/constants';

type LazyTableHandle = {
  getLazyParams: () => any;
  getEncodedParams: () => any;
};

const ContractAnniversaryTable: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const { showSuccess, showError } = useContext(ToastContext);
  const tableRef = useRef<LazyTableHandle>(null);
  const account = useAppSelector(state => state.authentication.account);
  const isAdminOrAssistant = hasAnyAuthority(account.authorities, [AUTHORITIES.ADMIN, AUTHORITIES.SUPER_ADMIN, AUTHORITIES.ASSISTANT]);

  const onShowContract = (data: any) => {
    if (data.id) {
      const temp = tableRef.current?.getEncodedParams();
      navigate('/create-contract/' + data.id + `?back=anniversary&p=${temp}`);
    }
  };

  const onInfoContract = (data: any) => {
    if (data.id) {
      const temp = tableRef.current?.getEncodedParams();
      navigate('/create-contract/' + data.id + `?back=anniversary&info&p=${temp}`);
    }
  };

  const handleExport = () => {
    setLoading(true);

    const lazyParams = tableRef.current?.getLazyParams();

    exportAnniversaryContractData(lazyParams.sortField, lazyParams.sortOrder, lazyParams.filter)
      .then(r => {
        showSuccess('Úspech', 'Dáta boli úspešne vyexportované.');
      })
      .catch(e => {
        console.error(e);
        showError('Chyba', e.response?.data?.detail);
      })
      .finally(() => setLoading(false));
  };

  return LazyTable({
    ref: tableRef,
    title: 'Výročia podpísania zmluvy o 6-10 týždňov',
    initialSortField: 'id',
    initialSortDirection: 'desc',
    createUrl: '/',
    serverUrl: '/api/contracts/anniversary-contracts',
    editActionAllowed: false,
    externalLoading: loading,
    additionalHeaderButtons: (
      <span>
        <div className="flex justify-content-between">
          <Button label="Exportovať dáta" severity="info" onClick={handleExport} />
        </div>
      </span>
    ),
    globalFilterFields: ['policyHolder.firstName', 'policyHolder.lastName', 'product.insuranceCompany.name', 'product.name'],
    columns: [
      { field: 'id', header: 'ID', sortable: true },
      { field: 'contractNr', header: 'Č. zmluvy', sortable: true },
      { field: 'proposalNr', header: 'Č. návrhu', sortable: true },
      { field: 'dateOfSignature', header: 'Dátum podpisu', sortable: true, formatter: formatFromLocalDate },
      { field: 'insuranceStartAt', header: 'Dátum začiatku poistenia', sortable: true, formatter: formatFromLocalDate },
      { field: 'createdDate', header: 'Vytvorené', sortable: true, formatter: formatDateFromInstant },
      {
        field: 'policyHolder.nameFullFormatted',
        header: 'Meno - poistník',
        sortable: false,
        formatter: (_, v) => boldNameFormatter(v?.policyHolder?.nameFullFormatted),
      },
      {
        field: 'concludedBy.nameFullFormatted',
        header: 'Sprostredkoval',
        sortable: false,
        formatter: (_, v) => boldNameFormatter(v?.concludedBy?.nameFullFormatted),
      },
      { field: 'product.insuranceCompany.name', header: 'Spoločnosť', sortable: true },
      { field: 'product.name', header: 'Produkt', sortable: true },
    ],
    additionalActions: [
      {
        icon: 'pi pi-info',
        tooltip: 'Info',
        onClick: onInfoContract,
      },
      ...(isAdminOrAssistant
        ? [
            {
              icon: 'pi pi-pencil',
              tooltip: 'Editovať',
              onClick: onShowContract,
            },
          ]
        : []),
    ],
  });
};

export default ContractAnniversaryTable;
